import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { useAllZones, useAllWards, usePermission } from '../hooks';
import { colors } from '../theme/theme';
import apiClient from '../api/client';

export const ZonesPage = () => {
  const { can } = usePermission();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [wardFilter, setWardFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedZone, setSelectedZone] = useState<any>(null);
  const [modules, setModules] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    ward_id: '',
    module_id: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const { zones, isLoading, error, refetch } = useAllZones({
    page: page + 1,
    per_page: rowsPerPage,
    search,
    ward_id: wardFilter !== 'all' ? wardFilter : undefined,
  });

  const { wards } = useAllWards({ page: 1, per_page: 100 });

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await apiClient.get('/admin/county/modules');

      // The actual structure is: { status: "success", data: { current_page: 1, data: [...], ... } }
      // So modules are at response.data.data (NOT response.data.data.data)
      const paginatedData = response.data.data;
      const modulesData = paginatedData?.data || paginatedData || [];


      if (Array.isArray(modulesData) && modulesData.length > 0) {
        setModules(modulesData);
      } else {
        console.warn('No modules found or invalid data structure');
      }
    } catch (err) {
      console.error('Failed to fetch modules:', err);
    }
  };

  const handleOpenDialog = (zone?: any) => {
    if (zone) {
      setSelectedZone(zone);
      setFormData({
        name: zone.name || '',
        ward_id: zone.ward_id?.toString() || '',
        module_id: zone.module_id?.toString() || '',
      });
    } else {
      setSelectedZone(null);
      setFormData({
        name: '',
        ward_id: '',
        module_id: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedZone(null);
    setFormData({
      name: '',
      ward_id: '',
      module_id: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        ward_id: parseInt(formData.ward_id),
        module_id: formData.module_id ? parseInt(formData.module_id) : null,
      };


      let response;
      if (selectedZone) {
        response = await apiClient.put(`/admin/county/zones/${selectedZone.id}`, data);
      } else {
        response = await apiClient.post('/admin/county/zones', data);
      }

      // apiClient returns response.data which is { status: 'success', data: {...} }
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: selectedZone ? 'Zone updated successfully' : 'Zone created successfully',
          severity: 'success',
        });
        handleCloseDialog();
        await refetch();
      } else {
        throw new Error(response.message || 'Failed to save zone');
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to save zone',
        severity: 'error',
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ward',
      headerName: 'Ward',
      flex: 1,
      minWidth: 150,
      valueGetter: (value: any) => value?.name || 'N/A',
    },
    {
      field: 'subcounty',
      headerName: 'Subcounty',
      flex: 1,
      minWidth: 150,
      valueGetter: (_: any, row: any) => row.ward?.subcounty?.name || 'N/A',
    },
    {
      field: 'module',
      headerName: 'Module',
      flex: 1,
      minWidth: 150,
      valueGetter: (value: any) => value?.name || 'N/A',
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Zones"
          subtitle="Manage zones and their administrative boundaries"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'My County', path: '/county-profile' },
            { label: 'Zones' },
          ]}
          action={
            can('locations.manage') ? <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Zone
            </Button> : undefined
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load zones. Please try again.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box display="flex" gap={2} mb={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search zones..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Ward</InputLabel>
                <Select
                  value={wardFilter}
                  onChange={(e) => setWardFilter(e.target.value)}
                  label="Ward"
                >
                  <MenuItem value="all">All Wards</MenuItem>
                  {wards?.data?.map((ward: any) => (
                    <MenuItem key={ward.id} value={ward.id}>
                      {ward.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={zones?.data || []}
                  columns={[
                    ...columns,
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      flex: 1,
                      minWidth: 120,
                      sortable: false,
                      renderCell: (params) => (
                        <Box>
                          {can('locations.manage') && <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(params.row)}
                            sx={{ color: colors.primary.main }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>}
                          {can('locations.manage') && <IconButton size="small" color="error">
                            <Delete fontSize="small" />
                          </IconButton>}
                        </Box>
                      ),
                    },
                  ]}
                  pageSizeOptions={[10, 25, 50]}
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setRowsPerPage(model.pageSize);
                  }}
                  rowCount={zones?.meta?.total || zones?.total || 0}
                  paginationMode="server"
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedZone ? 'Edit Zone' : 'Add Zone'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                required
                label="Zone Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <FormControl fullWidth required>
                <InputLabel>Ward</InputLabel>
                <Select
                  value={formData.ward_id}
                  onChange={(e) => setFormData({ ...formData, ward_id: e.target.value })}
                  label="Ward"
                >
                  <MenuItem value="">
                    <em>Select Ward</em>
                  </MenuItem>
                  {wards?.data?.map((ward: any) => (
                    <MenuItem key={ward.id} value={ward.id}>
                      {ward.name} ({ward.subcounty?.name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Module (Optional)</InputLabel>
                <Select
                  value={formData.module_id}
                  onChange={(e) => setFormData({ ...formData, module_id: e.target.value })}
                  label="Module (Optional)"
                >
                  <MenuItem value="">
                    <em>No Module</em>
                  </MenuItem>
                  {modules && modules.length > 0 ? (
                    modules.map((module: any) => (
                      <MenuItem key={module.id} value={module.id}>
                        {module.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>Loading modules...</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.name || !formData.ward_id}
            >
              {selectedZone ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};
